/**
 * Service de validation unifié avec schémas Zod
 * Conformément aux exigences 4.1, 4.2, 4.3 pour la cohérence de la validation
 */

import { z, ZodError, ZodIssue, ZodSchema } from 'zod';
import { CampaignStatus, ReferentStatus } from '../types/states';
import { 
  ValidationError, 
  ValidationErrorFactory,
  BusinessRuleViolationError,
  LimitExceededError 
} from '../errors/BusinessErrors';
import type { 
  Campaign, 
  Referent, 
  EvaluationCriterion, 
  CampaignFormData,
  EvaluationData,
  ValidationResult,
  UserFriendlyError,
  ValidationContext
} from '../types/unified';

/**
 * Service central de validation avec schémas Zod unifiés
 * Assure la cohérence des règles de validation entre frontend et backend
 */
export class ValidationService {
  private schemas: Map<string, ZodSchema> = new Map();
  private errorTranslations: Map<string, string> = new Map();

  constructor() {
    this.initializeSchemas();
    this.initializeErrorTranslations();
  }

  /**
   * Initialise tous les schémas de validation Zod
   */
  private initializeSchemas(): void {
    // Schéma pour les critères d'évaluation (limite 5-10)
    this.schemas.set('evaluationCriteria', z.object({
      criteria: z.array(z.object({
        id: z.string().min(1, 'ID du critère requis'),
        title: z.string()
          .min(1, 'Le titre du critère est obligatoire')
          .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
        description: z.string()
          .min(1, 'La description du critère est obligatoire')
          .max(1000, 'La description ne peut pas dépasser 1000 caractères'),
        weight: z.number()
          .min(1, 'Le poids doit être au minimum de 1')
          .max(10, 'Le poids ne peut pas dépasser 10'),
        category: z.string().optional(),
        is_required: z.boolean().default(true),
        order_index: z.number().min(0, 'L\'index d\'ordre doit être positif')
      }))
      .min(5, 'Au moins 5 critères d\'évaluation sont requis')
      .max(10, 'Maximum 10 critères d\'évaluation autorisés')
    }));

    // Schéma pour les données de campagne
    this.schemas.set('campaign', z.object({
      title: z.string()
        .min(1, 'Le titre de la campagne est obligatoire')
        .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
      description: z.string()
        .min(1, 'La description de la campagne est obligatoire')
        .max(2000, 'La description ne peut pas dépasser 2000 caractères'),
      status: z.nativeEnum(CampaignStatus, {
        errorMap: () => ({ message: 'Statut de campagne invalide' })
      }),
      candidate_email: z.string()
        .email('L\'adresse email du candidat n\'est pas valide')
        .min(1, 'L\'email du candidat est obligatoire'),
      candidate_name: z.string()
        .min(1, 'Le nom du candidat est obligatoire')
        .max(100, 'Le nom ne peut pas dépasser 100 caractères'),
      organization_id: z.string()
        .uuid('L\'ID d\'organisation doit être un UUID valide'),
      evaluation_criteria: z.array(z.string().uuid('ID de critère invalide'))
        .min(5, 'Au moins 5 critères d\'évaluation sont requis')
        .max(10, 'Maximum 10 critères d\'évaluation autorisés')
    }));

    // Schéma pour les données de formulaire de campagne
    this.schemas.set('campaignForm', z.object({
      title: z.string()
        .min(1, 'Le titre de la campagne est obligatoire')
        .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
      description: z.string()
        .min(1, 'La description de la campagne est obligatoire')
        .max(2000, 'La description ne peut pas dépasser 2000 caractères'),
      candidate_email: z.string()
        .email('L\'adresse email du candidat n\'est pas valide')
        .min(1, 'L\'email du candidat est obligatoire'),
      candidate_name: z.string()
        .min(1, 'Le nom du candidat est obligatoire')
        .max(100, 'Le nom ne peut pas dépasser 100 caractères'),
      evaluation_criteria: z.array(z.object({
        id: z.string().min(1),
        title: z.string().min(1).max(200),
        description: z.string().min(1).max(1000),
        weight: z.number().min(1).max(10),
        category: z.string().optional(),
        is_required: z.boolean().default(true),
        order_index: z.number().min(0)
      }))
      .min(5, 'Au moins 5 critères d\'évaluation sont requis')
      .max(10, 'Maximum 10 critères d\'évaluation autorisés'),
      referent_emails: z.array(z.string().email('Adresse email invalide'))
        .min(1, 'Au moins un référent est requis')
        .max(20, 'Maximum 20 référents autorisés'),
      organization_id: z.string()
        .uuid('L\'ID d\'organisation doit être un UUID valide')
    }));

    // Schéma pour les données de référent
    this.schemas.set('referent', z.object({
      email: z.string()
        .email('L\'adresse email n\'est pas valide')
        .min(1, 'L\'email du référent est obligatoire'),
      name: z.string()
        .max(100, 'Le nom ne peut pas dépasser 100 caractères')
        .optional(),
      status: z.nativeEnum(ReferentStatus, {
        errorMap: () => ({ message: 'Statut de référent invalide' })
      }),
      campaign_id: z.string()
        .uuid('L\'ID de campagne doit être un UUID valide'),
      organization_id: z.string()
        .uuid('L\'ID d\'organisation doit être un UUID valide')
    }));

    // Schéma pour les données d'évaluation
    this.schemas.set('evaluationData', z.object({
      ratings: z.record(z.string(), z.number().min(1).max(10))
        .refine(
          (ratings) => Object.keys(ratings).length >= 5,
          'Au moins 5 évaluations sont requises'
        ),
      comments: z.record(z.string(), z.string().max(2000, 'Commentaire trop long')),
      overall_rating: z.number()
        .min(1, 'La note globale doit être au minimum de 1')
        .max(10, 'La note globale ne peut pas dépasser 10')
        .optional(),
      overall_comment: z.string()
        .max(5000, 'Le commentaire global ne peut pas dépasser 5000 caractères')
        .optional(),
      contextual_info: z.object({
        collaboration_context: z.string().max(2000).optional(),
        relationship_duration: z.string().max(100).optional(),
        work_context: z.string().max(2000).optional(),
        additional_comments: z.string().max(5000).optional()
      }).optional(),
      submitted_at: z.string().datetime('Date de soumission invalide')
    }));

    // Schéma pour la validation des emails
    this.schemas.set('email', z.string()
      .email('L\'adresse email n\'est pas valide')
      .min(1, 'L\'adresse email est obligatoire')
    );

    // Schéma pour la validation des UUIDs
    this.schemas.set('uuid', z.string()
      .uuid('L\'identifiant doit être un UUID valide')
    );

    // Schéma pour les critères individuels
    this.schemas.set('evaluationCriterion', z.object({
      id: z.string().min(1, 'ID du critère requis'),
      title: z.string()
        .min(1, 'Le titre du critère est obligatoire')
        .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
      description: z.string()
        .min(1, 'La description du critère est obligatoire')
        .max(1000, 'La description ne peut pas dépasser 1000 caractères'),
      weight: z.number()
        .min(1, 'Le poids doit être au minimum de 1')
        .max(10, 'Le poids ne peut pas dépasser 10'),
      category: z.string().optional(),
      is_required: z.boolean().default(true),
      order_index: z.number().min(0, 'L\'index d\'ordre doit être positif')
    }));

    // Schéma pour la soumission de référents
    this.schemas.set('submitReferents', z.object({
      referents: z.array(z.object({
        name: z.string()
          .min(1, 'Le nom du référent est obligatoire')
          .max(100, 'Le nom ne peut pas dépasser 100 caractères'),
        email: z.string()
          .email('Format d\'email invalide')
          .max(255, 'L\'email ne peut pas dépasser 255 caractères'),
        position: z.string()
          .min(1, 'Le poste du référent est obligatoire')
          .max(200, 'Le poste ne peut pas dépasser 200 caractères'),
        relation_type: z.enum(['manager', 'collègue', 'client', 'autre'], {
          errorMap: () => ({ message: 'Type de relation invalide' })
        })
      }))
      .min(1, 'Au moins un référent est requis')
      .max(10, 'Maximum 10 référents autorisés')
    }));

    // Schéma pour l'évaluation de référent
    this.schemas.set('referentEvaluation', z.object({
      relationTypeConfirmed: z.string()
        .min(1, 'Le type de relation doit être confirmé'),
      collaborationStartDate: z.string()
        .min(1, 'La date de début de collaboration est obligatoire'),
      collaborationEndDate: z.string().optional(),
      ratings: z.record(z.string(), z.number()
        .min(1, 'La note doit être au minimum de 1')
        .max(10, 'La note ne peut pas dépasser 10')
      ).refine(
        (ratings) => Object.keys(ratings).length >= 5,
        'Au moins 5 critères doivent être évalués'
      ),
      criticalQuestions: z.object({
        wouldRecommend: z.boolean().nullable(),
        wouldRehire: z.boolean().nullable(),
        hasIncidents: z.boolean().nullable(),
        incidentsDescription: z.string().optional()
      }).optional(),
      audioFileUrl: z.string().url('URL audio invalide').optional(),
      audioTranscription: z.string().optional(),
      cleanedTranscription: z.string().optional(),
      transcriptionSentiment: z.string().optional(),
      transcriptionSentimentReason: z.string().optional(),
      transcriptionKeyPoints: z.array(z.string()).optional(),
      signedDocumentUrl: z.string().url('URL document invalide').optional()
    }));
  }

  /**
   * Initialise les traductions des messages d'erreur Zod
   */
  private initializeErrorTranslations(): void {
    this.errorTranslations.set('Required', 'Champ obligatoire');
    this.errorTranslations.set('String must contain at least 1 character(s)', 'Le texte ne peut pas être vide');
    this.errorTranslations.set('Array must contain at least 5 element(s)', 'Au moins 5 éléments sont requis');
    this.errorTranslations.set('Array must contain at most 10 element(s)', 'Maximum 10 éléments autorisés');
    this.errorTranslations.set('Expected string, received number', 'Texte attendu, nombre reçu');
    this.errorTranslations.set('Expected number, received string', 'Nombre attendu, texte reçu');
    this.errorTranslations.set('Expected boolean, received string', 'Valeur booléenne attendue, texte reçu');
    this.errorTranslations.set('Invalid email', 'Adresse email invalide');
    this.errorTranslations.set('Invalid uuid', 'Identifiant UUID invalide');
    this.errorTranslations.set('Invalid datetime', 'Date et heure invalides');
  }

  /**
   * Valide des données selon un schéma spécifique
   */
  async validate<T>(
    schemaName: string, 
    data: unknown, 
    context?: ValidationContext
  ): Promise<ValidationResult> {
    const schema = this.schemas.get(schemaName);
    if (!schema) {
      throw new ValidationError(`Schéma de validation '${schemaName}' introuvable`);
    }

    try {
      const validatedData = schema.parse(data) as T;
      
      // Validation des règles métier spécifiques
      const businessRuleErrors = await this.validateBusinessRules(
        schemaName, 
        validatedData, 
        context
      );

      if (businessRuleErrors.length > 0) {
        return {
          isValid: false,
          errors: businessRuleErrors,
          data: validatedData
        };
      }

      return {
        isValid: true,
        errors: [],
        data: validatedData
      };
    } catch (_error) {
      if (error instanceof ZodError) {
        const validationErrors = this.formatZodErrors(error.errors);
        return {
          isValid: false,
          errors: validationErrors
        };
      }
      throw error;
    }
  }

  /**
   * Valide spécifiquement les critères d'évaluation avec les limites 5-10
   */
  async validateEvaluationCriteria(
    criteria: EvaluationCriterion[]
  ): Promise<ValidationResult> {
    // Validation de base avec Zod
    const result = await this.validate('evaluationCriteria', { criteria });
    
    if (!result.isValid) {
      return result;
    }

    // Validations métier supplémentaires
    const businessErrors: UserFriendlyError[] = [];

    // Vérifier les doublons de titre
    const titles = criteria.map(c => c.title.toLowerCase());
    const duplicateTitles = titles.filter((title, index) => titles.indexOf(title) !== index);
    if (duplicateTitles.length > 0) {
      businessErrors.push({
        message: `Titres de critères en double: ${duplicateTitles.join(', ')}`,
        code: 'DUPLICATE_CRITERIA_TITLES',
        severity: 'error',
        field: 'criteria'
      });
    }

    // Vérifier la somme des poids
    const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
    if (totalWeight > 100) {
      businessErrors.push({
        message: `La somme des poids (${totalWeight}) ne peut pas dépasser 100`,
        code: 'TOTAL_WEIGHT_EXCEEDED',
        severity: 'error',
        field: 'criteria'
      });
    }

    // Vérifier les index d'ordre uniques
    const orderIndexes = criteria.map(c => c.order_index);
    const duplicateIndexes = orderIndexes.filter((index, i) => orderIndexes.indexOf(index) !== i);
    if (duplicateIndexes.length > 0) {
      businessErrors.push({
        message: 'Les index d\'ordre des critères doivent être uniques',
        code: 'DUPLICATE_ORDER_INDEXES',
        severity: 'error',
        field: 'criteria'
      });
    }

    if (businessErrors.length > 0) {
      return {
        isValid: false,
        errors: businessErrors,
        data: result.data
      };
    }

    return result;
  }

  /**
   * Valide les données d'évaluation d'un référent
   */
  async validateEvaluation(
    evaluationData: EvaluationData,
    requiredCriteriaIds?: string[]
  ): Promise<ValidationResult> {
    const result = await this.validate('evaluationData', evaluationData);
    
    if (!result.isValid) {
      return result;
    }

    const businessErrors: UserFriendlyError[] = [];

    // Vérifier que tous les critères requis sont évalués
    if (requiredCriteriaIds) {
      const missingCriteria = requiredCriteriaIds.filter(
        id => !(id in evaluationData.ratings)
      );
      
      if (missingCriteria.length > 0) {
        businessErrors.push({
          message: `Évaluations manquantes pour les critères: ${missingCriteria.join(', ')}`,
          code: 'MISSING_REQUIRED_EVALUATIONS',
          severity: 'error',
          field: 'ratings'
        });
      }
    }

    // Vérifier la cohérence entre ratings et comments
    const ratingIds = Object.keys(evaluationData.ratings);
    const commentIds = Object.keys(evaluationData.comments);
    
    const missingComments = ratingIds.filter(id => !commentIds.includes(id));
    if (missingComments.length > 0) {
      businessErrors.push({
        message: 'Un commentaire est requis pour chaque évaluation',
        code: 'MISSING_COMMENTS',
        severity: 'warning',
        field: 'comments'
      });
    }

    if (businessErrors.length > 0) {
      return {
        isValid: false,
        errors: businessErrors.filter(e => e.severity === 'error'),
        warnings: businessErrors.filter(e => e.severity === 'warning'),
        data: result.data
      };
    }

    return result;
  }

  /**
   * Valide les règles métier spécifiques selon le type de données
   */
  private async validateBusinessRules(
    schemaName: string,
    data: unknown,
    context?: ValidationContext
  ): Promise<UserFriendlyError[]> {
    const errors: UserFriendlyError[] = [];

    switch (schemaName) {
      case 'campaign':
      case 'campaignForm':
        // Règles métier pour les campagnes
        if (data.evaluation_criteria && data.evaluation_criteria.length < 5) {
          errors.push({
            message: 'Une campagne doit avoir au moins 5 critères d\'évaluation',
            code: 'INSUFFICIENT_CRITERIA',
            severity: 'error',
            field: 'evaluation_criteria'
          });
        }
        break;

      case 'referent':
        // Règles métier pour les référents
        if (context?.campaign_id && data.campaign_id !== context.campaign_id) {
          errors.push({
            message: 'Le référent doit appartenir à la campagne spécifiée',
            code: 'CAMPAIGN_MISMATCH',
            severity: 'error',
            field: 'campaign_id'
          });
        }
        break;

      case 'evaluationData':
        // Règles métier pour les évaluations
        const ratingsCount = Object.keys(data.ratings || {}).length;
        if (ratingsCount < 5) {
          errors.push({
            message: 'Au moins 5 évaluations sont requises pour valider une soumission',
            code: 'INSUFFICIENT_RATINGS',
            severity: 'error',
            field: 'ratings'
          });
        }
        break;
    }

    return errors;
  }

  /**
   * Formate les erreurs Zod en erreurs conviviales
   */
  private formatZodErrors(zodErrors: ZodIssue[]): UserFriendlyError[] {
    return zodErrors.map(error => {
      const field = error.path.join('.');
      let message = error.message;

      // Traduire les messages d'erreur courants
      const translation = this.errorTranslations.get(error.message);
      if (translation) {
        message = translation;
      }

      // Messages spécifiques selon le type d'erreur
      if (error.code === 'too_small') {
        if (error.type === 'array') {
          message = `Au moins ${error.minimum} éléments sont requis`;
        } else if (error.type === 'string') {
          message = `Au moins ${error.minimum} caractères sont requis`;
        } else if (error.type === 'number') {
          message = `La valeur doit être au minimum de ${error.minimum}`;
        }
      } else if (error.code === 'too_big') {
        if (error.type === 'array') {
          message = `Maximum ${error.maximum} éléments autorisés`;
        } else if (error.type === 'string') {
          message = `Maximum ${error.maximum} caractères autorisés`;
        } else if (error.type === 'number') {
          message = `La valeur ne peut pas dépasser ${error.maximum}`;
        }
      } else if (error.code === 'invalid_type') {
        const expectedType = this.translateType(error.expected);
        const receivedType = this.translateType(error.received);
        message = `${expectedType} attendu, ${receivedType} reçu`;
      }

      return {
        message,
        code: error.code.toUpperCase(),
        severity: 'error' as const,
        field: field || undefined,
        context: {
          zodError: error
        }
      };
    });
  }

  /**
   * Traduit les types Zod en français
   */
  private translateType(type: string): string {
    const typeTranslations: Record<string, string> = {
      'string': 'Texte',
      'number': 'Nombre',
      'boolean': 'Valeur booléenne',
      'array': 'Liste',
      'object': 'Objet',
      'null': 'Valeur nulle',
      'undefined': 'Valeur non définie'
    };

    return typeTranslations[type] || type;
  }

  /**
   * Valide un email simple
   */
  async validateEmail(email: string): Promise<boolean> {
    try {
      const result = await this.validate('email', email);
      return result.isValid;
    } catch {
      return false;
    }
  }

  /**
   * Valide un UUID simple
   */
  async validateUUID(uuid: string): Promise<boolean> {
    try {
      const result = await this.validate('uuid', uuid);
      return result.isValid;
    } catch {
      return false;
    }
  }

  /**
   * Obtient la liste des schémas disponibles
   */
  getAvailableSchemas(): string[] {
    return Array.from(this.schemas.keys());
  }

  /**
   * Ajoute un nouveau schéma de validation
   */
  addSchema(name: string, schema: ZodSchema): void {
    this.schemas.set(name, schema);
  }

  /**
   * Supprime un schéma de validation
   */
  removeSchema(name: string): boolean {
    return this.schemas.delete(name);
  }
}

/**
 * Instance singleton du service de validation
 * Permet d'utiliser le même service dans toute l'application
 */
export const validationService = new ValidationService();

/**
 * Utilitaires de validation rapide
 */
export const ValidationUtils = {
  /**
   * Valide rapidement des critères d'évaluation
   */
  async validateCriteria(criteria: EvaluationCriterion[]): Promise<ValidationResult> {
    return validationService.validateEvaluationCriteria(criteria);
  },

  /**
   * Valide rapidement une campagne
   */
  async validateCampaign(campaign: Partial<Campaign>): Promise<ValidationResult> {
    return validationService.validate('campaign', campaign);
  },

  /**
   * Valide rapidement un référent
   */
  async validateReferent(referent: Partial<Referent>): Promise<ValidationResult> {
    return validationService.validate('referent', referent);
  },

  /**
   * Valide rapidement des données d'évaluation
   */
  async validateEvaluationData(
    evaluationData: EvaluationData,
    requiredCriteriaIds?: string[]
  ): Promise<ValidationResult> {
    return validationService.validateEvaluation(evaluationData, requiredCriteriaIds);
  },

  /**
   * Vérifie si une valeur est un email valide
   */
  async isValidEmail(email: string): Promise<boolean> {
    return validationService.validateEmail(email);
  },

  /**
   * Vérifie si une valeur est un UUID valide
   */
  async isValidUUID(uuid: string): Promise<boolean> {
    return validationService.validateUUID(uuid);
  }
};